// AOV add-ons — Shipping Protection toggle + cart-emptied cleanup.
//
// Reuses the theme's existing cart AJAX contract exactly as implemented in theme.js
// (js/common/cart/line-item-quantity.js and js/common/product/product-form.js):
//   - POST {Shopify.routes.root}cart/add.js    to add a new line
//   - POST {Shopify.routes.root}cart/change.js to set/remove an existing line by line key
//   - dispatch `cart:prepare-bundled-sections` before either call so <cart-drawer> can
//     register itself for a Section Rendering API re-render
//   - dispatch `cart:change` on document.documentElement after a successful response so
//     <cart-drawer>, <free-shipping-bar>, <cart-dot>, etc. all update together
//
// No new cart-add path is introduced, and window.fetch is not overridden.

(function () {
  const SHIP_PROTECT_VARIANT_ID = 49094570377441;

  function bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  function loadingStart() {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  }

  function loadingEnd() {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  }

  // Mirrors the theme's own convention exactly (see onSubmit_fn in product-form.js and
  // changeLineItemQuantity_fn in line-item-quantity.js): on the full /cart page, a cart
  // mutation reloads the page instead of dispatching cart:change, because sections/main-cart.liquid
  // (unlike <cart-drawer>) has no listener that re-renders itself from the event payload. Without
  // this, a toggle flip or an orphan-line cleanup triggered from this page would leave the visible
  // order-summary table/subtotal stale until the next unrelated reload.
  function isCartPage() {
    return window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === "cart";
  }

  function dispatchCartChange(cart, baseEvent) {
    if (isCartPage()) {
      window.location.reload();
      return;
    }
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent, cart } })
    );
  }

  // Sets an EXISTING line to an absolute quantity (0 removes it). Mirrors
  // changeLineItemQuantity_fn in js/common/cart/line-item-quantity.js.
  async function cartChange(lineKey, quantity) {
    try {
      const sections = bundledSections();
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: lineKey, quantity, sections: sections.join(",") })
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  // Adds a NEW line. Mirrors onSubmit_fn in js/common/product/product-form.js, including the
  // follow-up cart.js fetch — /cart/add.js only returns the added line item, not the full cart.
  async function cartAdd(variantId, quantity) {
    try {
      const sections = bundledSections();
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ id: variantId, quantity, sections: sections.join(",") })
      });
      if (!response.ok) return null;
      const addResult = await response.json();
      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      cart.sections = addResult.sections;
      return cart;
    } catch (error) {
      return null;
    }
  }

  function findProtectionLine(cart) {
    return cart.items.find((item) => item.variant_id === SHIP_PROTECT_VARIANT_ID);
  }

  let cleanupInFlight = false;

  // Hard constraint: the protection line must never be left as an orphaned sole line item.
  // Runs on every cart:change (the same global event free-shipping-bar listens to) so it
  // self-corrects regardless of what removed the last physical item.
  async function cleanupOrphanedProtectionLine(cart) {
    if (!cart || !Array.isArray(cart.items) || cleanupInFlight) return;

    const protectionLine = findProtectionLine(cart);
    if (!protectionLine) return;

    const remaining = cart.items.reduce(
      (sum, item) => sum + (item.variant_id === SHIP_PROTECT_VARIANT_ID ? 0 : item.quantity),
      0
    );
    if (remaining > 0) return;

    cleanupInFlight = true;
    loadingStart();
    const updatedCart = await cartChange(protectionLine.key, 0);
    loadingEnd();
    cleanupInFlight = false;

    if (updatedCart) {
      dispatchCartChange(updatedCart, "shipping-protection:cleanup");
    }
  }

  document.addEventListener("cart:change", (event) => {
    cleanupOrphanedProtectionLine(event.detail && event.detail.cart);
  });

  class ShippingProtectionToggle extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('input[type="checkbox"]');
      if (!this.checkbox) return;

      this._onChange = this.onChange.bind(this);
      this.checkbox.addEventListener("change", this._onChange);

      // Self-heal on drawer open: if the server-rendered snapshot says this is already an
      // orphaned protection-only cart (e.g. the last physical item was removed from the full
      // cart page, so no cart:change ever fired here), clean it up immediately.
      if (this.dataset.orphaned === "true") {
        fetch(`${window.Shopify.routes.root}cart.js`)
          .then((response) => response.json())
          .then((cart) => cleanupOrphanedProtectionLine(cart))
          .catch(() => {});
      }
    }

    disconnectedCallback() {
      if (this.checkbox && this._onChange) {
        this.checkbox.removeEventListener("change", this._onChange);
      }
    }

    async onChange() {
      const checked = this.checkbox.checked;
      const lineKey = this.dataset.lineKey || "";
      const variantId = parseInt(this.dataset.variantId, 10);

      this.checkbox.disabled = true;
      loadingStart();

      let updatedCart = null;

      if (checked && !lineKey) {
        // Not currently in the cart — add it at qty 1.
        updatedCart = await cartAdd(variantId, 1);
      } else if (!checked && lineKey) {
        // Currently in the cart — remove the line entirely (never leave a qty-0 ghost).
        updatedCart = await cartChange(lineKey, 0);
      }
      // If checked+lineKey or !checked+!lineKey, the UI already matches the last known server
      // state — nothing to do (guards against double-fire / stale re-render races).

      loadingEnd();
      this.checkbox.disabled = false;

      if (updatedCart) {
        // Idempotency guard: never let the line exceed qty 1, no matter what came back.
        const line = findProtectionLine(updatedCart);
        if (line && line.quantity > 1) {
          loadingStart();
          const correctedCart = await cartChange(line.key, 1);
          loadingEnd();
          if (correctedCart) updatedCart = correctedCart;
        }
        dispatchCartChange(updatedCart, "shipping-protection:change");
      } else if (checked && !lineKey) {
        // The add request failed — don't let the toggle lie about the cart state.
        this.checkbox.checked = false;
      } else if (!checked && lineKey) {
        // The remove request failed — revert to checked.
        this.checkbox.checked = true;
      }
    }
  }

  if (!window.customElements.get("shipping-protection-toggle")) {
    window.customElements.define("shipping-protection-toggle", ShippingProtectionToggle);
  }
})();
